import i18n from '@/utils/i18n';
import React from 'react';
import {
  Button,
  Dialog,
  Icons,
  List,
  ShimmerText,
  Skeleton,
  Typography,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { FlatList } from 'react-native';
import { toSentenceCase } from '@/experiences/sales-portal/blocs/utils/text';
import { useTheme } from '@/contexts/theme-context';
import type { OnlineMenuItem } from '@/experiences/sales-portal/blocs/loaders/use-online-menu-loader';

const BODY_MIN_HEIGHT = 280;
const BODY_MAX_HEIGHT = 480;
const SKELETON_ROW_COUNT = 6;

interface OnlineMenuDialogProps {
  generating: boolean;
  generationFailed: boolean;
  hasError: boolean;
  isOpen: boolean;
  loading: boolean;
  menuItems: OnlineMenuItem[];
  onClose: () => void;
  onGenerate: () => void;
  onRetry: () => void;
  restaurantName?: string;
}

export const OnlineMenuDialog: React.FC<OnlineMenuDialogProps> = ({
  generating,
  generationFailed,
  hasError,
  isOpen,
  loading,
  menuItems,
  onClose,
  onGenerate,
  onRetry,
  restaurantName,
}) => {
  const theme = useTheme();

  const generateLabel = i18n.t(
    menuItems.length === 1
      ? 'sales_portal.create_order_guide.online_menu.generate'
      : 'sales_portal.create_order_guide.online_menu.generate_plural',
    { count: menuItems.length },
  );

  const empty = menuItems.length === 0;

  // `Dialog.Header`'s `headerAction` defaults to a close (X) button.
  // `undefined` keeps that default, `null` hides it while generating.
  const closeButton = generating ? null : undefined;

  const renderStep = (): {
    body: JSX.Element;
    bodyOwnsScroll?: boolean;
    footer: JSX.Element | null;
  } => {
    if (generating) {
      return {
        body: (
          <YStack
            accessibilityRole="progressbar"
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <ShimmerText size="$md">
              {i18n.t('sales_portal.create_order_guide.online_menu.generating')}
            </ShimmerText>
          </YStack>
        ),
        footer: null,
      };
    }

    if (loading) {
      return {
        body: (
          <YStack accessibilityRole="progressbar">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
              <YStack
                gap={theme.sk.spacing.spaceSmall}
                key={index}
                paddingVertical={theme.sk.spacing.spaceMedium}
              >
                <Skeleton borderRadius={4} height={13} width={180} />
                <Skeleton borderRadius={4} height={11} width={280} />
              </YStack>
            ))}
          </YStack>
        ),
        footer: null,
      };
    }

    if (hasError) {
      return {
        body: (
          <YStack
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <ErrorState
              description={i18n.t(
                'sales_portal.create_order_guide.online_menu.error_description',
              )}
              onPress={onRetry}
              title={i18n.t(
                'sales_portal.create_order_guide.online_menu.error_title',
              )}
            />
          </YStack>
        ),
        footer: null,
      };
    }

    if (generationFailed) {
      return {
        body: (
          <YStack
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <ErrorState
              description={i18n.t(
                'sales_portal.create_order_guide.generation_failed_description',
              )}
              icon={
                <Icons.CircleDashed
                  color={theme.sk.color.icon.primary}
                  size="$48"
                  strokeWidth={1}
                />
              }
              onPress={onGenerate}
              title={i18n.t(
                'sales_portal.create_order_guide.generation_failed_title',
              )}
            />
          </YStack>
        ),
        footer: null,
      };
    }

    if (empty) {
      return {
        body: (
          <YStack
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <EmptyState
              description={i18n.t(
                'sales_portal.create_order_guide.online_menu.empty_description',
              )}
              iconElement={
                <Icons.CircleDashed
                  color={theme.sk.color.icon.primary}
                  size="$48"
                  strokeWidth={1}
                />
              }
              title={i18n.t(
                'sales_portal.create_order_guide.online_menu.empty_title',
              )}
            />
          </YStack>
        ),
        footer: null,
      };
    }

    return {
      body: (
        <List>
          <FlatList
            data={menuItems}
            keyExtractor={(menuItem, index) => `${menuItem.name}-${index}`}
            renderItem={({ item: menuItem }) => (
              <List.Item>
                <List.Item.Content>
                  <Typography
                    color={theme.sk.color.text.primary}
                    fontWeight="$medium"
                    size="$sm"
                  >
                    {toSentenceCase(menuItem.name)}
                  </Typography>
                  <Typography color={theme.sk.color.text.secondary} size="$xs">
                    {menuItem.ingredients.map(toSentenceCase).join(', ')}
                  </Typography>
                </List.Item.Content>
              </List.Item>
            )}
            style={{ maxHeight: BODY_MAX_HEIGHT }}
          />
        </List>
      ),
      bodyOwnsScroll: true,
      footer: (
        <>
          <Button onPress={onClose} size="$md" variant="tertiary">
            {i18n.t('cancel')}
          </Button>
          <Button onPress={onGenerate} size="$md" variant="primary">
            {generateLabel}
          </Button>
        </>
      ),
    };
  };

  const { body, footer, bodyOwnsScroll } = renderStep();

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && !generating) {
          onClose();
        }
      }}
      open={isOpen}
    >
      <Dialog.Content>
        <Dialog.Header headerAction={closeButton}>
          <YStack gap={2}>
            <Dialog.Title>
              {i18n.t('sales_portal.create_order_guide.online_menu')}
            </Dialog.Title>
            {restaurantName ? (
              <Typography color={theme.sk.color.text.secondary} size="$sm">
                {restaurantName}
              </Typography>
            ) : null}
          </YStack>
        </Dialog.Header>
        {bodyOwnsScroll ? body : <Dialog.Body>{body}</Dialog.Body>}
        {footer ? <Dialog.Footer fade>{footer}</Dialog.Footer> : null}
      </Dialog.Content>
    </Dialog>
  );
};
